import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { buildInfo } from 'build-info';
import { AppUpdateAvailable } from './app-update.interface';
import { LegacyServiceWorkerCleanupService } from './legacy-service-worker-cleanup.service';

const RELOADED_FOR_BUILD_KEY = 'portal.reloaded-for-build';
const SNOOZED_BUILD_KEY = 'portal.snoozed-build';

const UPDATE_POLL_INTERVAL_MS = 60 * 60_000;
const UPDATE_SNOOZE_MS = 4 * 60 * 60_000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
    private readonly _updateAvailableSubject =
        new BehaviorSubject<AppUpdateAvailable | null>(null);
    private _reloadInProgress = false;
    private _checkInFlight = false;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _legacyCleanup: LegacyServiceWorkerCleanupService
    ) {
        if (!environment.appUpdateEnabled) {
            return;
        }

        void this._legacyCleanup.cleanupOnce();
        void this.checkForFreshFrontend();

        const visibleOrFocused$ = merge(
            fromEvent(window, 'focus'),
            fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.visibilityState === 'visible')
            )
        );

        visibleOrFocused$.subscribe(() => {
            void this.checkForFreshFrontend();
        });

        // SignalR keeps a macrotask alive, so ApplicationRef.isStable never becomes true.
        // Run the backup timer outside Angular so it does not depend on that state.
        this._ngZone.runOutsideAngular(() => {
            window.setInterval(() => {
                void this.checkForFreshFrontend();
            }, UPDATE_POLL_INTERVAL_MS);
        });
    }

    get updateAvailable$() {
        return this._updateAvailableSubject.asObservable();
    }

    async reloadNow(): Promise<boolean> {
        const buildId = this._updateAvailableSubject.value?.buildId;
        if (!buildId) {
            return false;
        }

        return await this.reloadIntoFreshFrontend(buildId);
    }

    snoozeUpdate(): void {
        const buildId = this._updateAvailableSubject.value?.buildId;
        if (!buildId) {
            return;
        }

        try {
            sessionStorage.setItem(
                SNOOZED_BUILD_KEY,
                JSON.stringify({
                    buildId,
                    until: Date.now() + UPDATE_SNOOZE_MS,
                })
            );
        } catch {
            // Storage can be unavailable in private mode. Hide until the next check.
        }

        this.publishUpdate(null);
    }

    private publishUpdate(update: AppUpdateAvailable | null): void {
        this._ngZone.run(() => this._updateAvailableSubject.next(update));
    }

    private async checkForFreshFrontend(): Promise<boolean> {
        if (this._reloadInProgress) {
            return true;
        }

        if (this._checkInFlight) {
            return false;
        }

        this._checkInFlight = true;

        try {
            const remoteBuildId = await this.fetchRemoteBuildId();
            if (!this.isNewBuild(remoteBuildId)) {
                this.publishUpdate(null);
                return false;
            }

            if (this.alreadyReloadedFor(remoteBuildId)) {
                return false;
            }

            if (this.isSnoozed(remoteBuildId)) {
                return false;
            }

            this.publishUpdate({ buildId: remoteBuildId });
            return true;
        } finally {
            this._checkInFlight = false;
        }
    }

    private isNewBuild(buildId: string | null): buildId is string {
        return !!buildId && buildId !== buildInfo.buildId;
    }

    private isSnoozed(buildId: string): boolean {
        try {
            const rawValue = sessionStorage.getItem(SNOOZED_BUILD_KEY);
            if (!rawValue) {
                return false;
            }

            const value = JSON.parse(rawValue) as {
                buildId?: string;
                until?: number;
            };
            return value.buildId === buildId && Number(value.until) > Date.now();
        } catch {
            return false;
        }
    }

    private async fetchRemoteBuildId(): Promise<string | null> {
        try {
            const response = await fetch(
                `/version.json?ngsw-bypass=true&t=${Date.now()}`,
                {
                    cache: 'no-store',
                    headers: { 'Cache-Control': 'no-cache' }
                }
            );

            if (!response.ok) {
                return null;
            }

            const payload = (await response.json()) as { buildId?: string };
            const remoteBuildId = payload?.buildId?.trim();

            return remoteBuildId || null;
        } catch {
            return null;
        }
    }

    private alreadyReloadedFor(buildId: string): boolean {
        try {
            return sessionStorage.getItem(RELOADED_FOR_BUILD_KEY) === buildId;
        } catch {
            return false;
        }
    }

    private markReloadedFor(buildId: string): void {
        try {
            sessionStorage.setItem(RELOADED_FOR_BUILD_KEY, buildId);
        } catch {
            // Private mode can block sessionStorage; in-memory guard still applies.
        }
    }

    private async reloadIntoFreshFrontend(remoteBuildId: string): Promise<boolean> {
        if (this._reloadInProgress) {
            return true;
        }

        if (this.alreadyReloadedFor(remoteBuildId)) {
            return false;
        }

        this._reloadInProgress = true;
        this.publishUpdate(null);
        this.markReloadedFor(remoteBuildId);
        await this._legacyCleanup.cleanup();
        document.location.reload();
        return true;
    }
}
